import { ChevronDownIcon, Download02Icon, LinkExternal01Icon } from "@/icons";
import { Button } from "@/kit/ui/button";
import { Divider } from "@/kit/ui/divider";
import {
  ResponsiveMenu,
  ResponsiveMenuContent,
  ResponsiveMenuTrigger,
} from "@/kit/ui/responsive-menu";
import React, { PropsWithChildren, isValidElement, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  fetchQuery,
  graphql,
  useFragment,
  useRelayEnvironment,
} from "react-relay";
import { NavLink } from "react-router";
import download from "../utils/download";
import { assertNever } from "../utils/helpers";
import {
  ArchiveKind,
  ProjectDownloadButtonDownloadArchiveQuery as DownloadArchiveQueryType,
} from "./__generated__/ProjectDownloadButtonDownloadArchiveQuery.graphql";
import {
  ProjectDownloadButtonFragment$data,
  ProjectDownloadButtonFragment$key,
} from "./__generated__/ProjectDownloadButtonFragment.graphql";
import { ActivityIndicator } from "./ActivityIndicator";
import Code from "./Code";
import DropdownItem from "./DropdownItem";

const ProjectDownloadButtonFragment = graphql`
  fragment ProjectDownloadButtonFragment on ProjectVersionFile {
    id
    kind
    extension
    projectVersion {
      project {
        competition {
          slug
        }
      }
    }
  }
`;

export interface ProjectDownloadButtonProps extends PropsWithChildren {
  fragment: ProjectDownloadButtonFragment$key;
}

export default function ProjectDownloadButton(
  props: ProjectDownloadButtonProps,
) {
  const fragment = useFragment(ProjectDownloadButtonFragment, props.fragment);

  return (
    <ResponsiveMenu>
      <ResponsiveMenuTrigger asChild>
        <DownloadButton>
          {isValidElement(props.children) ? (
            props.children
          ) : (
            <DownloadButtonLabel {...fragment} />
          )}
        </DownloadButton>
      </ResponsiveMenuTrigger>
      <ResponsiveMenuContent
        className="p-0 lg:min-w-(--radix-popper-anchor-width)"
        align="end"
      >
        <div className="flex flex-col p-4 gap-4">
          <DownloadPopoverContent {...fragment} />
        </div>
      </ResponsiveMenuContent>
    </ResponsiveMenu>
  );
}

const DownloadButton = React.forwardRef<
  HTMLButtonElement,
  React.PropsWithChildren
>(({ children, ...rest }, ref) => (
  <Button ref={ref} variant="secondary" className="space-x-2" {...rest}>
    <Download02Icon className="inline-block" />
    <span>{children}</span>
    <ChevronDownIcon className="inline-block" />
  </Button>
));

function DownloadButtonLabel({ kind }: ProjectDownloadButtonFragment$data) {
  switch (kind) {
    case "TEMPLATE":
      return <FormattedMessage defaultMessage="Download template" />;
    case "DATA":
      return <FormattedMessage defaultMessage="Download data" />;
    case "PACKAGE":
      return <FormattedMessage defaultMessage="Download use-case" />;
    case "SUBMISSION_EVALUATION":
      return (
        <FormattedMessage defaultMessage="Download submission evaluation" />
      );
    default:
      assertNever(kind);
  }
}

function DownloadPopoverContent({
  id,
  kind,
  extension,
  projectVersion: {
    project: { competition },
  },
}: ProjectDownloadButtonFragment$data) {
  switch (kind) {
    case "TEMPLATE":
      return <TemplateInstructions competition={competition.slug} />;
    case "DATA":
    case "PACKAGE":
    case "SUBMISSION_EVALUATION":
      return (
        <>
          <DownloadArchive
            projectVersionFileId={id}
            archKind="TAR"
            extension={extension ?? "tar.gz"}
          />
          <DownloadArchive
            projectVersionFileId={id}
            archKind="ZIP"
            extension="zip"
          />
        </>
      );
    default:
      assertNever(kind);
  }
}

const TemplateInstructions = ({ competition }: { competition: string }) => (
  <div className="space-y-4">
    <p className="text-gray-900 text-sm font-normal leading-5">
      <FormattedMessage defaultMessage="To dowmload template Follow these simple steps:" />
    </p>

    <ol className="list-decimal list-inside space-y-4 bg-grey-100 rounded-lg p-4">
      <li>
        <a
          href="https://pypi.org/project/aqora/"
          target="_blank"
          className="text-brand-500 underline inline-flex flex-row items-center space-x-1"
        >
          <span>
            <FormattedMessage defaultMessage="Install Aqora CLI" />
          </span>
          <LinkExternal01Icon />
        </a>
      </li>
      <Divider />
      <li>
        <FormattedMessage defaultMessage="Run the following command:" />{" "}
        <Code content={`aqora template ${competition}`} className="bg-white" />
      </li>
    </ol>
    <p className="text-gray-900 text-sm font-normal leading-5">
      <FormattedMessage
        defaultMessage="For more details, check out <a>our tutorial</a>."
        values={{
          a: (chunks) => (
            <NavLink
              to={`/challenges/h2-groundstate-energy/tracks/h2-groundstate-energy`}
              className="text-brand-500 underline"
            >
              {chunks}
            </NavLink>
          ),
        }}
      />
    </p>
  </div>
);

interface DownloadArchiveProps {
  projectVersionFileId: ID;
  archKind: ArchiveKind;
  extension: string;
}

const DownloadArchiveQuery = graphql`
  query ProjectDownloadButtonDownloadArchiveQuery(
    $id: ID!
    $archKind: ArchiveKind!
  ) {
    node(id: $id) {
      ... on ProjectVersionFile {
        downloadUrl(archKind: $archKind)
      }
    }
  }
`;

function DownloadArchive({
  projectVersionFileId: id,
  archKind,
  extension,
}: DownloadArchiveProps) {
  const relay = useRelayEnvironment();
  const [pending, setPending] = useState(false);

  const onClick = async (evt: React.MouseEvent) => {
    evt.preventDefault();

    setPending(true);
    try {
      const query = await fetchQuery<DownloadArchiveQueryType>(
        relay,
        DownloadArchiveQuery,
        {
          id,
          archKind,
        },
      ).toPromise();

      if (!query || !query.node.downloadUrl) {
        throw new Error(`Unexpected empty response`);
      }

      download(query.node.downloadUrl);
    } finally {
      setPending(false);
    }
  };

  return (
    <DropdownItem
      className="flex gap-2 items-center"
      onClick={onClick}
      disabled={pending}
      icon={pending && <ActivityIndicator size={16} />}
    >
      <FormattedMessage
        defaultMessage="Download <em>{extension}</em>"
        values={{ extension, em: (text) => <code>{text}</code> }}
      />
    </DropdownItem>
  );
}
